import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// 404
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ChangePinPage = lazy(() => import('@/pages/auth/ChangePinPage'));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));

// Trips
const MyTripsPage = lazy(() => import('@/pages/trips/MyTripsPage'));
const NewTripPage = lazy(() => import('@/pages/trips/NewTripPage'));
const TripDetailPage = lazy(() => import('@/pages/trips/TripDetailPage'));

// Approvals
const ApprovalsPage = lazy(() => import('@/pages/approvals/ApprovalsPage'));

// Employees
const EmployeeListPage = lazy(() => import('@/pages/employees/EmployeeListPage'));
const EmployeeFormPage = lazy(() => import('@/pages/employees/EmployeeFormPage'));
const EmployeeDetailPage = lazy(() => import('@/pages/employees/EmployeeDetailPage'));
const EmployeeTripHistoryPage = lazy(() => import('@/pages/employees/EmployeeTripHistoryPage'));

// Disputes
const DisputesPage = lazy(() => import('@/pages/disputes/DisputesPage'));

// Others
const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage'));
const BranchesPage = lazy(() => import('@/pages/branches/BranchesPage'));
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'));
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const TripSummaryPage = lazy(() => import('@/pages/analytics/TripSummaryPage'));
const LiveMapPage = lazy(() => import('@/pages/live/LiveMapPage'));
const AuditPage = lazy(() => import('@/pages/audit/AuditPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));

function PageLoader() {
  return <LoadingSpinner className="py-24" size="lg" message="Loading..." />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          element={
            <AuthGuard>
              <AppLayout />
            </AuthGuard>
          }
        >
          {/* Dashboard */}
          <Route path="/" element={<DashboardPage />} />

          {/* Change PIN */}
          <Route path="/change-pin" element={<ChangePinPage />} />

          {/* Trips */}
          <Route path="/trips" element={<MyTripsPage />} />
          <Route path="/trips/new" element={<NewTripPage />} />
          <Route path="/trips/:id" element={<TripDetailPage />} />

          {/* Approvals - manager/admin */}
          <Route
            path="/approvals"
            element={
              <AuthGuard allowedRoles={['manager', 'admin']}>
                <ApprovalsPage />
              </AuthGuard>
            }
          />

          {/* Employees - admin/hr */}
          <Route
            path="/employees"
            element={
              <AuthGuard allowedRoles={['admin', 'hr', 'manager']}>
                <EmployeeListPage />
              </AuthGuard>
            }
          />
          {/* Only Admin can create employees — HR can view/edit profiles but not add new ones */}
          <Route
            path="/employees/new"
            element={
              <AuthGuard allowedRoles={['admin']}>
                <EmployeeFormPage />
              </AuthGuard>
            }
          />
          <Route
            path="/employees/:id"
            element={
              <AuthGuard allowedRoles={['admin', 'hr', 'manager']}>
                <EmployeeDetailPage />
              </AuthGuard>
            }
          />
          <Route
            path="/employees/:id/trips"
            element={
              <AuthGuard allowedRoles={['admin', 'manager']}>
                <EmployeeTripHistoryPage />
              </AuthGuard>
            }
          />
          {/* Managers cannot edit employee records — Admin and HR only */}
          <Route
            path="/employees/:id/edit"
            element={
              <AuthGuard allowedRoles={['admin', 'hr']}>
                <EmployeeFormPage />
              </AuthGuard>
            }
          />

          {/*
           * Disputes — managers can VIEW disputes on their team's trips,
           * but only Admin can resolve or dismiss them.
           * The DisputesPage itself conditionally hides action buttons for managers.
           */}
          <Route
            path="/disputes"
            element={
              <AuthGuard allowedRoles={['manager', 'admin']}>
                <DisputesPage />
              </AuthGuard>
            }
          />

          {/* Clients - admin */}
          <Route
            path="/clients"
            element={
              <AuthGuard allowedRoles={['admin']}>
                <ClientsPage />
              </AuthGuard>
            }
          />

          {/* Branches - admin */}
          <Route
            path="/branches"
            element={
              <AuthGuard allowedRoles={['admin']}>
                <BranchesPage />
              </AuthGuard>
            }
          />

          {/* Reports - manager/admin/hr */}
          <Route
            path="/reports"
            element={
              <AuthGuard allowedRoles={['manager', 'admin', 'hr']}>
                <ReportsPage />
              </AuthGuard>
            }
          />

          {/* Analytics - manager/admin/hr */}
          <Route
            path="/analytics"
            element={
              <AuthGuard allowedRoles={['manager', 'admin', 'hr']}>
                <AnalyticsPage />
              </AuthGuard>
            }
          />

          {/* Trip Summary - manager/admin/hr */}
          <Route
            path="/trip-summary"
            element={
              <AuthGuard allowedRoles={['manager', 'admin', 'hr']}>
                <TripSummaryPage />
              </AuthGuard>
            }
          />

          {/* Live Map - manager/admin */}
          <Route
            path="/live"
            element={
              <AuthGuard allowedRoles={['manager', 'admin']}>
                <LiveMapPage />
              </AuthGuard>
            }
          />

          {/* Audit - admin */}
          <Route
            path="/audit"
            element={
              <AuthGuard allowedRoles={['admin']}>
                <AuditPage />
              </AuthGuard>
            }
          />

          {/* Notifications */}
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>

        {/* Catch-all — show a proper 404 instead of silently redirecting */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
